import React, { useState, useEffect, useCallback } from 'react';

// API Base URL (আপনার ব্যাকএন্ড যে পোর্টে চলছে)
const API_URL = 'https://api.muktihospital.com/api/authorities';

// Modal Component
const AuthorityModal = ({ isOpen, onClose, onSave, authority }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    // যখন মোডাল খোলা হবে এবং এডিটের জন্য ডেটা থাকবে, তখন ফর্মটি পূরণ করুন
    if (authority) {
      setName(authority.name || '');
      setDescription(authority.description || '');
    } else {
      // নতুন যোগ করার জন্য ফর্মটি খালি করুন
      setName('');
      setDescription('');
    }
    setError(''); // আগের error মুছে ফেলুন
  }, [authority, isOpen]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Name is required.');
      return;
    }
    
    // onSave ফাংশনকে ডেটা পাস করুন
    await onSave({ ...authority, name, description });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          {authority ? 'Edit Authority' : 'Add New Authority'}
        </h2>
        
        {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
        
        <div className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              id="description"
              rows="3"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            ></textarea>
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};


// Main App Component
export default function Authority() {
  const [authorities, setAuthorities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentAuthority, setCurrentAuthority] = useState(null);

  // ডেটা লোড করার জন্য ফাংশন
  const fetchAuthorities = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(API_URL);
      if (!response.ok) {
        throw new Error('Something went wrong!');
      }
      const data = await response.json();
      setAuthorities(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // কম্পোনেন্ট প্রথমবার লোড হওয়ার সময় ডেটা আনুন
  useEffect(() => {
    fetchAuthorities();
  }, [fetchAuthorities]);

  // নতুন Authority যোগ বা এডিট করার জন্য মোডাল খোলা
  const handleOpenModal = (authority = null) => {
    setCurrentAuthority(authority);
    setIsModalOpen(true);
  };

  // মোডাল বন্ধ করা
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentAuthority(null);
  };

  // Authority সেভ (Add/Update) করার ফাংশন
  const handleSaveAuthority = async (authorityData) => {
    const isEditing = !!authorityData.id;
    const url = isEditing ? `${API_URL}/${authorityData.id}` : API_URL;
    const method = isEditing ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            name: authorityData.name,
            description: authorityData.description,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to ${isEditing ? 'update' : 'add'} authority.`);
      }
      
      handleCloseModal();
      await fetchAuthorities(); // তালিকা রিফ্রেশ করুন

    } catch (err) {
      alert(err.message); // সহজভাবে এরর দেখানো হলো
    }
  };

  // Authority ডিলেট করার ফাংশন
  const handleDeleteAuthority = async (id) => {
    if (window.confirm('Are you sure you want to delete this authority?')) {
        try {
            const response = await fetch(`${API_URL}/${id}`, {
                method: 'DELETE',
            });
            if (!response.ok) {
                throw new Error('Failed to delete authority.');
            }
            await fetchAuthorities(); // তালিকা রিফ্রেশ করুন
        } catch(err) {
            alert(err.message);
        }
    }
  };

  return (
    <div className="bg-gray-100 min-h-screen">
      <div className="container mx-auto p-4 sm:p-6 lg:p-8">
        <header className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Authorities</h1>
          <button
            onClick={() => handleOpenModal()}
            className="bg-indigo-600 text-white font-semibold py-2 px-4 rounded-lg shadow-md hover:bg-indigo-700 transition duration-300"
          >
            + Add New Authority
          </button>
        </header>

        {isLoading && <p className="text-center text-gray-500">Loading...</p>}
        {error && <p className="text-center text-red-500 bg-red-100 p-3 rounded-lg">{error}</p>}
        
        {!isLoading && !error && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {authorities.length > 0 ? (
                  authorities.map((auth) => (
                    <tr key={auth.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{auth.name}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-600">{auth.description || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                        <button onClick={() => handleOpenModal(auth)} className="text-indigo-600 hover:text-indigo-900">Edit</button>
                        <button onClick={() => handleDeleteAuthority(auth.id)} className="text-red-600 hover:text-red-900">Delete</button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3" className="px-6 py-4 text-center text-gray-500">
                      No authorities found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AuthorityModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveAuthority}
        authority={currentAuthority}
      />
    </div>
  );
}